import React, { useState, useEffect, useCallback, useRef } from 'react';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import api from '../services/api';
import useDebouncedValue from '../hooks/useDebouncedValue';

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    priority: '',
    department: ''
  });
  const debouncedSearch = useDebouncedValue(filters.search, 350);
  const hasLoadedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const authScope = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');

      if (user.role === 'super_admin') {
        return {
          activeCompanyName: localStorage.getItem('activeCompanyName') || 'Seçili firma'
        };
      }

      return {
        activeCompanyName: user.company || 'Kendi firmanız'
      };
    } catch (error) {
      return { activeCompanyName: 'Firma bilgisi yok' };
    }
  })();

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'duyuru',
    priority: 'normal',
    targetAudience: 'tümü',
    specificDepartments: [],
    tags: [],
    expiryDate: ''
  });

  const fetchAnnouncements = useCallback(async () => {
    const requestId = ++requestSequenceRef.current;
    try {
      if (hasLoadedRef.current) setRefreshing(true);
      else setLoading(true);
      const response = await api.getAnnouncements({
        search: debouncedSearch,
        type: filters.type,
        priority: filters.priority,
        department: filters.department
      });
      if (requestId === requestSequenceRef.current && response.success) {
        setAnnouncements(response.announcements);
      }
    } catch (error) {
      if (requestId !== requestSequenceRef.current) return;
      console.error('Duyurular yüklenemedi:', error);
      showError('Duyurular yüklenemedi.');
    } finally {
      if (requestId === requestSequenceRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [debouncedSearch, filters.department, filters.priority, filters.type, showError]);

  // Duyuruları çek
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.createAnnouncement(formData);
      if (response.success) {
        showSuccess('Duyuru başarıyla oluşturuldu!');
        setShowCreateForm(false);
        setFormData({
          title: '',
          content: '',
          type: 'duyuru',
          priority: 'normal',
          targetAudience: 'tümü',
          specificDepartments: [],
          tags: [],
          expiryDate: ''
        });
        fetchAnnouncements();
      } else {
        showError(response.message || 'Duyuru oluşturulamadı.');
      }
    } catch (error) {
      console.error('Duyuru oluşturma hatası:', error);
      showError('Duyuru oluşturulurken hata oluştu.');
    }
  };

  const handleLike = async (announcementId) => {
    try {
      const response = await api.toggleAnnouncementLike(announcementId);
      if (response.success) {
        fetchAnnouncements();
      }
    } catch (error) {
      console.error('Beğeni hatası:', error);
      showError('Beğeni işlemi başarısız.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Belirtilmemiş';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'acil': return 'danger';
      case 'yüksek': return 'warning';
      case 'normal': return 'primary';
      case 'düşük': return 'secondary';
      default: return 'primary';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'duyuru': return 'bx-bullhorn';
      case 'haber': return 'bx-news';
      case 'genel': return 'bx-info-circle';
      case 'acil': return 'bx-error';
      default: return 'bx-bullhorn';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="modern-loading">
              <div className="text-center">
                <div className="modern-spinner spinner-border" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <h5 className="text-muted mb-3 mt-3">Duyurular yükleniyor...</h5>
                <p className="text-muted">Lütfen bekleyin</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid documents-page">
      {/* Doküman Merkezi tarzı başlık */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4 corporate-documents-hero">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <h3 className="fw-bold text-dark mb-1">Duyurular</h3>
                <small className="badge bg-light text-dark">
                  Aktif firma: {authScope.activeCompanyName}
                </small>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="badge bg-primary rounded-pill px-3 py-2">
                  <i className="bx bx-bullhorn me-1"></i>
                  {announcements.length} Duyuru
                </div>
                <button
                  className="btn btn-modern-primary px-4 py-2"
                  onClick={() => setShowCreateForm(true)}
                >
                  <i className="bx bx-plus me-2"></i>
                  Yeni Duyuru
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4">
            <div className="row g-3">
              <div className="col-lg-4 col-md-6">
                <div className="position-relative">
                  <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                  <input 
                    type="text" 
                    className="form-control modern-filter-input ps-5" 
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Başlık veya içerik ara..."
                  />
                </div>
              </div>
              <div className="col-lg-2 col-md-6">
                <select 
                  className="form-select modern-filter-input" 
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                >
                  <option value="">Tüm Tipler</option>
                  <option value="duyuru">Duyuru</option>
                  <option value="haber">Haber</option>
                  <option value="genel">Genel</option>
                  <option value="acil">Acil</option>
                </select>
              </div>
              <div className="col-lg-2 col-md-6">
                <select 
                  className="form-select modern-filter-input" 
                  name="priority"
                  value={filters.priority}
                  onChange={handleFilterChange}
                >
                  <option value="">Tüm Öncelikler</option>
                  <option value="acil">Acil</option>
                  <option value="yüksek">Yüksek</option>
                  <option value="normal">Normal</option>
                  <option value="düşük">Düşük</option>
                </select>
              </div>
              <div className="col-lg-2 col-md-6">
                <select 
                  className="form-select modern-filter-input" 
                  name="department"
                  value={filters.department}
                  onChange={handleFilterChange}
                >
                  <option value="">Tüm Departmanlar</option>
                  <option value="Ulaştırma Projeleri">Ulaştırma Projeleri</option>
                  <option value="İnsan Kaynakları">İnsan Kaynakları</option>
                  <option value="Muhasebe">Muhasebe</option>
                  <option value="Pazarlama">Pazarlama</option>
                </select>
              </div>
              <div className="col-lg-2 col-md-6">
                <button 
                  className="btn btn-modern-secondary w-100"
                  onClick={() => setFilters({
                    search: '',
                    type: '',
                    priority: '',
                    department: ''
                  })}
                >
                  <i className="bx bx-refresh me-1"></i>
                  Temizle
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Announcements List */}
      <div className="row search-results-region" aria-busy={refreshing}>
        <div className="col-12">
          {refreshing && (
            <div className="search-refresh-indicator" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Sonuçlar güncelleniyor…
            </div>
          )}
          {announcements.length === 0 ? (
            <div className="modern-empty-state text-center py-5">
              <div className="mb-4">
                <i className="bx bx-bullhorn text-muted" style={{ fontSize: '64px', opacity: '0.3' }}></i>
              </div>
              <h4 className="text-muted mb-3">Henüz duyuru yok</h4>
              <p className="text-muted mb-4">İlk duyuruyu oluşturmak için yukarıdaki butona tıklayın.</p>
              <button 
                className="btn btn-modern-primary px-4"
                onClick={() => setShowCreateForm(true)}
              >
                <i className="bx bx-plus me-2"></i>
                İlk Duyuruyu Oluştur
              </button>
            </div>
          ) : (
            <div className="row g-4">
              {announcements.map((announcement) => (
                <div key={announcement._id} className="col-12">
                  <div className="modern-card p-4 announcement-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center">
                        <div className={`rounded-circle d-flex align-items-center justify-content-center me-3`} 
                             style={{ 
                               width: '48px', 
                               height: '48px',
                               background: `linear-gradient(135deg, ${getPriorityColor(announcement.priority) === 'danger' ? '#ff6b6b' : 
                                 getPriorityColor(announcement.priority) === 'warning' ? '#ffd93d' : 
                                 getPriorityColor(announcement.priority) === 'primary' ? '#4ecdc4' : '#95a5a6'}, 
                                 ${getPriorityColor(announcement.priority) === 'danger' ? '#ee5a52' : 
                                 getPriorityColor(announcement.priority) === 'warning' ? '#f9ca24' : 
                                 getPriorityColor(announcement.priority) === 'primary' ? '#45b7aa' : '#7f8c8d'})`
                             }}>
                          <i className={`bx ${getTypeIcon(announcement.type)} text-white`} style={{ fontSize: '20px' }}></i>
                        </div>
                        <div>
                          <h5 className="mb-1 fw-bold text-dark">{announcement.title}</h5>
                          <div className="d-flex align-items-center gap-2">
                            {announcement.isPinned && (
                              <span className="badge bg-warning text-dark rounded-pill px-2 py-1">
                                <i className="bx bx-pin me-1"></i>
                                Sabitlenmiş
                              </span>
                            )}
                            <span className={`badge bg-${getPriorityColor(announcement.priority)} rounded-pill px-2 py-1`}>
                              {announcement.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-outline-primary rounded-pill px-3 py-2"
                          onClick={() => handleLike(announcement._id)}
                          style={{ fontSize: '14px' }}
                        >
                          <i className="bx bx-heart me-1"></i>
                          {announcement.likeCount || 0}
                        </button>
                        <span
                          className="btn btn-outline-secondary rounded-pill px-3 py-2 disabled"
                          style={{ fontSize: '14px' }}
                          aria-disabled="true"
                        >
                          <i className="bx bx-comment me-1"></i>
                          {announcement.commentCount || 0}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-muted mb-3 lh-base">{announcement.content}</p>
                    
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center text-muted small">
                        <div className="d-flex align-items-center me-4">
                          <i className="bx bx-user me-1"></i>
                          <span className="fw-medium">{announcement.author ? `${announcement.author.firstName} ${announcement.author.lastName}` : 'Silinmiş kullanıcı'}</span>
                        </div>
                        <div className="d-flex align-items-center me-4">
                          <i className="bx bx-building me-1"></i>
                          <span>{announcement.department}</span>
                        </div>
                        <div className="d-flex align-items-center me-4">
                          <i className="bx bx-time me-1"></i>
                          <span>{formatDate(announcement.publishDate)}</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <i className="bx bx-show me-1"></i>
                          <span>{announcement.viewCount || 0} görüntüleme</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modern Create Announcement Modal */}
      {showCreateForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content modern-modal-content">
              <div className="modal-header modern-modal-header">
                <h4 className="modal-title fw-bold text-dark">Yeni Duyuru Oluştur</h4>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowCreateForm(false)}
                  style={{ fontSize: '20px' }}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body pt-0">
                  <div className="row g-3">
                    <div className="col-md-8">
                      <div className="mb-3">
                        <label className="form-label modern-form-label">Başlık</label>
                        <input 
                          type="text" 
                          className="form-control modern-input" 
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          placeholder="Duyuru başlığını girin"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label modern-form-label">Tip</label>
                        <select 
                          className="form-select modern-input" 
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                        >
                          <option value="duyuru">Duyuru</option>
                          <option value="haber">Haber</option>
                          <option value="genel">Genel</option>
                          <option value="acil">Acil</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label modern-form-label">Öncelik</label>
                        <select 
                          className="form-select modern-input" 
                          name="priority"
                          value={formData.priority}
                          onChange={handleInputChange}
                        >
                          <option value="düşük">Düşük</option>
                          <option value="normal">Normal</option>
                          <option value="yüksek">Yüksek</option>
                          <option value="acil">Acil</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label modern-form-label">Hedef Kitle</label>
                        <select 
                          className="form-select modern-input" 
                          name="targetAudience"
                          value={formData.targetAudience}
                          onChange={handleInputChange}
                        >
                          <option value="tümü">Tümü</option>
                          <option value="yöneticiler">Yöneticiler</option>
                          <option value="çalışanlar">Çalışanlar</option>
                          <option value="belirli_departman">Belirli Departman</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium text-dark">İçerik</label>
                    <textarea 
                      className="form-control modern-input" 
                      rows="6"
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      required
                      placeholder="Duyuru içeriğini girin"
                      style={{ resize: 'none' }}
                    ></textarea>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label modern-form-label">Son Geçerlilik Tarihi</label>
                        <input 
                          type="date" 
                          className="form-control modern-input" 
                          name="expiryDate"
                          value={formData.expiryDate}
                          onChange={handleInputChange}
                        />
                        <small className="modern-form-text">Opsiyonel</small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label modern-form-label">Etiketler</label>
                        <input 
                          type="text" 
                          className="form-control modern-input" 
                          name="tags"
                          value={formData.tags.join(', ')}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                          }))}
                          placeholder="etiket1, etiket2, etiket3"
                        />
                        <small className="modern-form-text">Virgülle ayırın</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer modern-modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-modern-secondary px-4"
                    onClick={() => setShowCreateForm(false)}
                  >
                    İptal
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-modern-primary px-4"
                  >
                    <i className="bx bx-save me-2"></i>
                    Duyuru Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default AnnouncementsPage;
